/**
 * Qué puede HACER el cliente cuando el cotizador de envío falla.
 *
 * ── POR QUÉ ESTO ES UN MÓDULO Y NO UN `if` DENTRO DEL COMPONENTE ────────────
 * Antes los cuatro fallos caían en el MISMO cartel gris: "No pudimos calcular el
 * envío ahora mismo. Intenta de nuevo en un momento." + botón de reintentar.
 * Para la mitad de ellos eso es mentira.
 *
 * El caso que lo destapó: si la dirección de RECOGIDA del local no tiene ZIP, el
 * proveedor rechaza la cotización, la ruta responde 502 y el cliente ve
 * "intenta de nuevo en un momento". Ese reintento no puede funcionar NUNCA —
 * el mismo request va a fallar igual mil veces. El cliente queda en un loop
 * hasta que abandona el pedido.
 *
 * Palabras del dueño: "eso de anunciar un error no debería ser; si está muy
 * lejos debe decir que está muy lejos y no llegan a esa zona, y ofrecer el
 * pickup."
 *
 * Dos reglas, y las dos se testean acá abajo:
 *  1. `canRetry` SOLO donde reintentar puede funcionar de verdad.
 *  2. El pickup se ofrece SIEMPRE — ningún fallo termina sin salida.
 */

export type DeliveryErrorKind = 'not_available' | 'rate_limited' | 'network' | 'quote_failed'

export type DeliveryErrorUx = {
  /** `warn` = cambió el plan, esto no se arregla solo. `neutral` = transitorio. */
  tone: 'warn' | 'neutral'
  canRetry: boolean
  canChangeAddress: boolean
}

export const DELIVERY_ERROR_UX: Record<DeliveryErrorKind, DeliveryErrorUx> = {
  // Transitorios de VERDAD: esperar y reintentar ES la acción correcta.
  rate_limited: { tone: 'neutral', canRetry: true, canChangeAddress: false },
  network: { tone: 'neutral', canRetry: true, canChangeAddress: false },
  // 502: el proveedor no pudo resolver el envío para esa dirección. Reintentar no
  // cambia nada; lo que sí, es otra dirección o pasar a recoger.
  quote_failed: { tone: 'warn', canRetry: false, canChangeAddress: true },
  // 409: el local tiene el envío apagado. Ni reintentar ni cambiar de dirección
  // sirven de nada — lo único real es el pickup.
  not_available: { tone: 'warn', canRetry: false, canChangeAddress: false },
}

/**
 * Status HTTP de /api/delivery/quote → tipo de fallo.
 * Un status que no conocemos cae en `quote_failed`: la opción conservadora es NO
 * prometer que reintentar arregla algo.
 */
export function deliveryErrorKindFromStatus(status: number): DeliveryErrorKind {
  if (status === 409) return 'not_available'
  if (status === 429) return 'rate_limited'
  return 'quote_failed'
}
